/**
 * Telegram Mini App integration.
 *
 * When the user opens https://t.me/<bot>/app?startapp=…, Telegram
 * renders the Mini App inside its WebView, injects
 * `window.Telegram.WebApp`, and gives us:
 *   - `initData`     — signed query string the BE validates via HMAC
 *                      (see services/api/tg_miniapp.py).
 *   - `initDataUnsafe.start_param` — the value after `?startapp=` in
 *                      the deep link (e.g. `invite_abc123`).
 *   - `themeParams`  — colors picked from the user's TG theme; let TG
 *                      decide so the Mini App blends with their app.
 *
 * In a normal browser, `window.Telegram` is undefined and these helpers
 * are no-ops — the legacy login flows (widget / OIDC) handle that path.
 */

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe: {
          query_id?: string;
          user?: {
            id: number;
            first_name?: string;
            last_name?: string;
            username?: string;
            language_code?: string;
          };
          auth_date?: number;
          hash?: string;
          start_param?: string;
        };
        version: string;
        platform: string;
        colorScheme: 'light' | 'dark';
        themeParams: Record<string, string | undefined>;
        ready: () => void;
        expand: () => void;
        close: () => void;
        isExpanded: boolean;
        onEvent: (eventType: string, handler: () => void) => void;
        offEvent: (eventType: string, handler: () => void) => void;
      };
    };
  }
}

/** True iff this page is rendered inside a Telegram Mini App container
 *  AND we got a signed initData payload to validate. Mere presence of
 *  `window.Telegram.WebApp` is not enough — TG's preview iframe injects
 *  the SDK but leaves initData empty, which would fail BE validation. */
export function isMiniAppContext(): boolean {
  return Boolean(window.Telegram?.WebApp?.initData);
}

/** True when Telegram launched us as a Mini App, detectable SYNCHRONOUSLY on
 *  first paint. Telegram appends `tgWebAppData` / `tgWebAppVersion` /
 *  `tgWebAppPlatform` to the URL when it opens the app — these are present
 *  immediately, BEFORE the `async` telegram-web-app.js SDK has executed and
 *  parsed them into `window.Telegram.WebApp.initData`. Without this, a cold
 *  first open evaluates `isMiniAppContext()` before the SDK loads → falls
 *  through to /login; the SDK then loads (cached) so the SECOND open works —
 *  the "login on first open, autologin on second" bug. */
export function hasMiniAppLaunchParams(): boolean {
  const hay = window.location.hash + '&' + window.location.search;
  return /(?:^|[#&?])tgWebApp(?:Data|Version|Platform)=/.test(hay);
}

/** Resolve once the async SDK has populated `initData` (it parses the launch
 *  params on load), or after `timeoutMs`. Polls rather than subscribing to an
 *  SDK event because we may run BEFORE the SDK exists (nothing to subscribe to
 *  yet). Returns true iff initData became available. */
export function waitForMiniAppInitData(timeoutMs = 3000): Promise<boolean> {
  if (isMiniAppContext()) return Promise.resolve(true);
  return new Promise((resolve) => {
    const start = Date.now();
    const id = window.setInterval(() => {
      if (isMiniAppContext()) {
        window.clearInterval(id);
        resolve(true);
      } else if (Date.now() - start >= timeoutMs) {
        window.clearInterval(id);
        resolve(false);
      }
    }, 50);
  });
}

/** Tell Telegram we're ready (hides their loading splash) + take the
 *  full viewport. Safe to call from non-Mini-App contexts — the guard
 *  short-circuits in a normal browser. */
export function applyMiniAppViewport(): void {
  const wa = window.Telegram?.WebApp;
  if (!wa) return;
  try {
    wa.ready();
    if (!wa.isExpanded) wa.expand();
  } catch {
    // Older WebApp SDK versions may not have expand(); ignore.
  }
}

/** Read the raw initData string for BE submission. */
export function getMiniAppInitData(): string {
  return window.Telegram?.WebApp?.initData ?? '';
}

/** Parse the `start_param` (the value after `?startapp=` in the deep
 *  link). Returns null when not in Mini App context or no start_param. */
export function getMiniAppStartParam(): string | null {
  const sp = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
  return sp || null;
}

/** Convention: invite deep links use `?startapp=invite_<token>`. The
 *  underscore separator survives Telegram's startapp validation
 *  (alphanumeric + underscore only, per
 *  https://core.telegram.org/api/links#mini-app-links). Returns the
 *  invite token portion when the start_param matches; null otherwise. */
export function extractInviteTokenFromStartParam(): string | null {
  const sp = getMiniAppStartParam();
  if (!sp) return null;
  const match = sp.match(/^invite_(.+)$/);
  return match ? match[1] : null;
}

/** Blend the app with Telegram's window chrome. Inside a Mini App, the
 *  user has a system theme (light/dark) AND possibly a custom theme
 *  (TG accent colors). Override the few CSS vars that show through the
 *  app shell so the seam between TG chrome and our content disappears.
 *  Brand colors (--color-primary) stay Linza-blue.
 *
 *  Cleans up its own listener on unmount via the returned dispose fn.
 *  Idempotent — calling twice replaces the prior listener. */
export function applyMiniAppTheme(): () => void {
  const wa = window.Telegram?.WebApp;
  if (!wa) return () => {};

  const root = document.documentElement;

  function apply() {
    if (!wa) return;
    // Only blend if the user hasn't pinned a Linza theme. With a
    // pinned preference, applying TG's themeParams would mismatch
    // the .dark class (e.g. forced-dark app + light TG params =
    // black text on near-black bg). Pinned wins; sidebar toggle
    // continues to function inside the Mini App.
    const storedPref = window.localStorage.getItem('theme');
    if (storedPref === 'light' || storedPref === 'dark') {
      return;
    }

    if (wa.colorScheme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');

    // Bridge themeParams → our CSS vars. Only override the small set
    // that shows through the app chrome; brand colors stay our own.
    const tp = wa.themeParams || {};
    const map: Record<string, string | undefined> = {
      '--color-background': tp.bg_color,
      '--color-foreground': tp.text_color,
      '--color-card': tp.section_bg_color || tp.secondary_bg_color,
      '--color-popover': tp.section_bg_color || tp.secondary_bg_color,
      '--color-muted': tp.secondary_bg_color,
      '--color-muted-foreground': tp.subtitle_text_color || tp.hint_color,
      '--color-border': tp.section_separator_color,
      '--color-sidebar': tp.header_bg_color || tp.bg_color,
    };
    for (const [k, v] of Object.entries(map)) {
      if (v) root.style.setProperty(k, v);
      else root.style.removeProperty(k);
    }
  }

  apply();
  wa.onEvent('themeChanged', apply);
  return () => {
    if (wa) wa.offEvent('themeChanged', apply);
  };
}
